import { Button, Form, Spinner } from 'react-bootstrap';
import Select, { type SingleValue, type StylesConfig } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import {
  SOURCE_OPTIONS,
  toggleSource,
  type CountryOption,
  type MassiveFormValues,
  type SelectOption,
  type SubmitButtonState
} from './massiveFormConfig';

const selectControlStyle: StylesConfig<SelectOption, false> = {
  container: base => ({ ...base, width: '100%' }),
  control: base => ({
    ...base,
    minHeight: '38px',
    fontSize: '0.8125rem',
    boxShadow: 'none',
    borderRadius: 0
  }),
  menu: base => ({ ...base, fontSize: '0.8125rem' })
};

type MassiveFormFooterProps = {
  values: MassiveFormValues;
  setFieldValue: (field: string, value: unknown) => void;
  countries?: CountryOption[];
  languages?: SelectOption[];
  selectedCountry: CountryOption;
  selectedLanguage: SelectOption;
  onCountryChange: (country: CountryOption) => void;
  onLanguageChange: (language: SelectOption) => void;
  submitState: SubmitButtonState;
};

const MassiveFormFooter = ({
  values,
  setFieldValue,
  countries,
  languages,
  selectedCountry,
  selectedLanguage,
  onCountryChange,
  onLanguageChange,
  submitState
}: MassiveFormFooterProps) => (
  <div className="d-flex flex-column gap-3 w-100 massive-footer-options">
    <SearchTypeOptions values={values} setFieldValue={setFieldValue} />
    <SourcePicker values={values} setFieldValue={setFieldValue} />

    <div className="input-group input-group-sm w-100">
        <div className="flex-fill" style={{ minWidth: 0 }}>
          <Select<CountryOption, false>
            isSearchable
            onChange={(option: SingleValue<CountryOption>) => {
              if (option) onCountryChange(option);
            }}
            options={countries}
            classNamePrefix="react-select"
            value={selectedCountry}
            maxMenuHeight={200}
            styles={{
              ...selectControlStyle,
              control: (base, state) => ({
                ...(selectControlStyle.control?.(base, state) ?? base),
                borderRadius: '0 5px 5px 0'
              })
            }}
            components={{
              Option: OptionLayoutPlace,
              SingleValue: SingleValueFlag
            }}
          />
        </div>
        <div className="flex-fill" style={{ minWidth: 0 }}>
          <Select<SelectOption, false>
            isSearchable
            onChange={(option: SingleValue<SelectOption>) => {
              if (option) onLanguageChange(option);
            }}
            options={languages}
            classNamePrefix="react-select"
            value={selectedLanguage}
            maxMenuHeight={200}
            styles={selectControlStyle}
            components={{
              Option: OptionLayoutPlace,
              SingleValue: SingleValueFlag
            }}
          />
        </div>
        <ChildrensTooltip placement="top" html={submitState.tooltip}>
          <Button
            variant="falcon-primary"
            type="submit"
            className="d-flex align-items-center justify-content-center gap-1 px-4 rounded-start-0 flex-shrink-0"
            style={{ minWidth: '6.5rem' }}
            disabled={submitState.disabled}
          >
            {submitState.showLoading ? (
              <>
                <Spinner style={{ width: 14, height: 14 }} />
                در حال جستجو...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="search" />
                جستجو
              </>
            )}
          </Button>
        </ChildrensTooltip>
    </div>

    <style>{`
      .massive-footer-options .form-check-label {
        margin-bottom: 0 !important;
        padding-inline-start: 0.2rem !important;
      }
      .massive-footer-options .form-check {
        margin-bottom: 0;
        min-height: auto;
        display: inline-flex;
        align-items: center;
        gap: 0;
      }
    `}</style>
  </div>
);

type FormFieldProps = {
  values: MassiveFormValues;
  setFieldValue: (field: string, value: unknown) => void;
};

const fieldLabelClass =
  'fs-0 text-700 flex-shrink-0 d-inline-flex align-items-center gap-1 mb-0';

const SearchTypeOptions = ({ values, setFieldValue }: FormFieldProps) => (
  <div className="d-flex align-items-center gap-2 flex-wrap">
    <span className={fieldLabelClass} style={{ minWidth: '3.5rem' }}>
      نوع
      <HelpTooltip
        html={keywordResearchTooltips.massive.mode}
        className="ms-1 fs--1"
      />
    </span>
    <div className="d-flex align-items-center gap-2 flex-wrap form-label-nogutter">
      <Form.Check
        inline
        type="checkbox"
        id="massive-nested"
        className="mb-0 d-flex align-items-center"
        checked={!values.with_questions}
        onChange={() => {
          setFieldValue('with_questions', false);
        }}
        label={<span className="fs--1 cursor-pointer">تودرتو</span>}
      />
      <Form.Check
        inline
        type="checkbox"
        id="massive-questions"
        className="mb-0 d-flex align-items-center"
        checked={values.with_questions}
        onChange={() =>
          setFieldValue('with_questions', !values.with_questions)
        }
        label={<span className="fs--1 cursor-pointer">سوالی</span>}
      />
    </div>
  </div>
);

const SourcePicker = ({ values, setFieldValue }: FormFieldProps) => (
  <div className="d-flex align-items-center gap-2 flex-wrap">
    <span className={fieldLabelClass} style={{ minWidth: '3.5rem' }}>
      منبع
      <HelpTooltip
        html={keywordResearchTooltips.massive.source}
        className="ms-1 fs--1"
      />
    </span>
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {SOURCE_OPTIONS.map(({ value, icon: Icon, title, disabled }) => {
        const selected = !disabled && values.sources.includes(value);
        const button = (
          <button
            type="button"
            title={disabled ? undefined : title}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              setFieldValue('sources', toggleSource(values.sources, value));
            }}
            className={`btn btn-sm p-1 border d-inline-flex align-items-center justify-content-center ${
              disabled
                ? 'border-200 bg-transparent'
                : selected
                  ? 'border-primary bg-primary text-white'
                  : 'border-300 bg-transparent opacity-50'
            }`}
            style={disabled ? { opacity: 1 } : undefined}
          >
            <Icon
              size="20px"
              color={
                selected
                  ? '#ffffff'
                  : disabled
                    ? 'var(--falcon-gray-400)'
                    : undefined
              }
            />
          </button>
        );

        if (disabled) {
          return (
            <ChildrensTooltip key={value} placement="top" html="به زودی">
              <span className="d-inline-flex">{button}</span>
            </ChildrensTooltip>
          );
        }

        return (
          <span key={value} className="d-inline-flex">
            {button}
          </span>
        );
      })}
    </div>
  </div>
);

export default MassiveFormFooter;
